<template>
  <div class="calendar-tab">

    <v-row class="mb-8">
      <v-col cols="12" class="d-flex align-center" style="gap: 12px;">
        <div class="view-toggle-segmented">
          <button type="button" class="view-toggle-segment view-toggle-segment--left"
            :class="{ 'view-toggle-segment--active': viewMode === 'calendar' }" @click="setViewMode('calendar')">
            <v-icon size="20"
              :color="viewMode === 'calendar' ? 'plusBackground' : 'plusGreyDarken1'">mdi-calendar-month-outline</v-icon>
            <span class="view-toggle-label">{{ $t('message.calendar_admin_plus.view_calendar') }}</span>
          </button>
          <button type="button" class="view-toggle-segment view-toggle-segment--right"
            :class="{ 'view-toggle-segment--active': viewMode === 'list' }" @click="setViewMode('list')">
            <v-icon size="20"
              :color="viewMode === 'list' ? 'plusBackground' : 'plusGreyDarken1'">mdi-format-list-bulleted</v-icon>
            <span class="view-toggle-label">{{ $t('message.calendar_admin_plus.view_list') }}</span>
          </button>
        </div>
        <v-btn elevation="0" small :loading="loadingCalendar" @click="loadCalendar()">
          <v-icon class="mr-1">mdi-sync</v-icon>
          {{ $t("message.refresh") }}
        </v-btn>
        <v-btn v-if="todaysVisitors.length > 0 && !isSmallerDisplay()" elevation="0" small color="orange" outlined
          @click="todaysVisitorsDialog = true">
          <v-icon class="mr-1">mdi-account-multiple-outline</v-icon>
          {{ todaysVisitors.length }} {{ $t("message.visitors_today") }}
        </v-btn>
      </v-col>
    </v-row>
    <v-row v-if="todaysVisitors.length > 0 && isSmallerDisplay()">
      <v-col>
        <v-btn elevation="0" small color="orange" outlined block @click="todaysVisitorsDialog = true">
          <v-icon class="mr-1">mdi-account-multiple-outline</v-icon>
          {{ todaysVisitors.length }} {{ $t("message.visitors_today") }}
        </v-btn>
      </v-col>
    </v-row>

    <v-dialog v-model="todaysVisitorsDialog" max-width="500">
      <v-card>
        <v-card-title>
          {{ todaysVisitors.length }} {{ $t("message.visitors_today") }}
        </v-card-title>
        <v-list>
          <v-list-item v-for="(v, i) in todaysVisitors" :key="'todays_visitor_' + i" link
            @click="todaysVisitorsDialog = false; goToVisitor(v.visitorId, v.visitorName)">
            <v-list-item-content>
              <v-list-item-title>{{ v.visitorName }}</v-list-item-title>
              <v-list-item-subtitle>{{ v.className }} · {{ formatTodaysVisitorTime(v.classTime)
              }}</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="todaysVisitorsDialog = false">{{ $t("message.close") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-card-actions
      class="d-flex flex-wrap mb-4 rounded-lg mobile-header-border justify-space-between align-center mb-md-7">
      <v-row class="d-flex align-center" no-gutters>


        <v-col cols="12" lg="10" order="2" class="align-center d-lg-flex pa-6 pa-lg-0" style="gap: 8px;"
          :class="displayFilters ? '' : 'd-lg-flex d-none'">

          <div class="text-subtitle-2 plusGreyDarken1--text text-center text-lg-left ">{{
            $t('message.calendar_admin_plus.filter_by_event_type') }}</div>


          <!-- Regular Class -->
          <v-tooltip top :disabled="$vuetify.breakpoint.smAndDown">
            <template v-slot:activator="{ on, attrs }">
              <span :style="{ backgroundColor: isDarkMode() ? '#FFFFFF' : '#FFEBEE' }"
                class="plusGreyLighten3 event-type-filter-chip d-flex justify-left justify-lg-center align-center pt-1 pb-1 pl-3 pr-3 mt-lg-0 mt-4 rounded-xl"
                style="flex-wrap:nowrap;" v-bind="attrs" v-on="on" role="button" tabindex="0"
                :class="{ 'event-type-filter-chip--off': !isEventTypeSelected('f5f5f5') }"
                @click.stop="toggleEventTypeFilter('f5f5f5')" @keydown.enter.prevent="toggleEventTypeFilter('f5f5f5')"
                @keydown.space.prevent="toggleEventTypeFilter('f5f5f5')">
                <div class="plusGreyDarken1 rounded-circle" style="height: 14px; width: 14px;"></div>
                <div class="ml-2 font-roboto text-subtitle-2">{{
                  $t('message.calendar_admin_plus.event_type_regular_class') }}</div>
              </span>
            </template>
            <span class="d-none d-lg-block">{{ $t("message.calendar_admin_plus.regular_schedule_explained") }}</span>
          </v-tooltip>

          <v-tooltip top :disabled="$vuetify.breakpoint.smAndDown">
            <template v-slot:activator="{ on, attrs }">
              <span
                class="plusPurpleLighten5 event-type-filter-chip d-flex justify-left justify-lg-center align-center  pt-1 pb-1 pl-3 pr-3 mt-lg-0 mt-4 rounded-xl wrap-nowrap"
                v-bind="attrs" v-on="on" role="button" tabindex="0"
                :class="{ 'event-type-filter-chip--off': !isEventTypeSelected('8d70b2') }"
                @click.stop="toggleEventTypeFilter('8d70b2')" @keydown.enter.prevent="toggleEventTypeFilter('8d70b2')"
                @keydown.space.prevent="toggleEventTypeFilter('8d70b2')">
                <div class="plusPurpleDarken3 rounded-circle" style="height: 14px; width: 14px;"></div>
                <div class="ml-2 font-roboto text-subtitle-2 ">{{
                  $t('message.calendar_admin_plus.event_type_one_time_class') }}</div>
              </span>
            </template>
            <span>{{ $t("message.calendar_admin_plus.admin_event_explained") }}</span>
          </v-tooltip>



          <v-tooltip top :disabled="$vuetify.breakpoint.smAndDown">
            <template v-slot:activator="{ on, attrs }">
              <span
                class="plusAmberLighten5 event-type-filter-chip d-flex justify-left justify-lg-center align-center pt-1 pb-1 pl-3 pr-3 mt-lg-0 mt-4 rounded-xl"
                v-bind="attrs" v-on="on" role="button" tabindex="0"
                :class="{ 'event-type-filter-chip--off': !isEventTypeSelected('fd9927') }"
                @click.stop="toggleEventTypeFilter('fd9927')" @keydown.enter.prevent="toggleEventTypeFilter('fd9927')"
                @keydown.space.prevent="toggleEventTypeFilter('fd9927')">
                <div class="plusYellowDarken4 rounded-circle" style="height: 14px; width: 14px;"></div>
                <div class="ml-2 font-roboto text-subtitle-2">{{
                  $t('message.calendar_admin_plus.event_type_class_with_visitor') }}</div>
              </span>
            </template>
            <span>{{ $t("message.calendar_admin_plus.with_visitor_event_explained") }}</span>
          </v-tooltip>


          <v-tooltip top :disabled="$vuetify.breakpoint.smAndDown">
            <template v-slot:activator="{ on, attrs }">
              <span
                class="plusGreenLighten5 event-type-filter-chip d-flex justify-left justify-lg-center align-center pt-1 pb-1 pl-3 pr-3 mt-lg-0 mt-4 rounded-xl"
                v-bind="attrs" v-on="on" role="button" tabindex="0"
                :class="{ 'event-type-filter-chip--off': !isEventTypeSelected('ec7063') }"
                @click.stop="toggleEventTypeFilter('ec7063')" @keydown.enter.prevent="toggleEventTypeFilter('ec7063')"
                @keydown.space.prevent="toggleEventTypeFilter('ec7063')">
                <div class="plusGreen rounded-circle" style="height: 14px; width: 14px;"></div>
                <div class="ml-2 font-roboto text-subtitle-2">{{
                  $t('message.calendar_admin_plus.event_type_bjjlink_connect_events') }}</div>
              </span>
            </template>
            <span>{{ $t("message.calendar_admin_plus.social_event_explained") }}</span>
          </v-tooltip>

          <v-tooltip top :disabled="$vuetify.breakpoint.smAndDown">
            <template v-slot:activator="{ on, attrs }">
              <span :style="{ backgroundColor: '#FFEBEE' }"
                class="event-type-filter-chip d-flex justify-left justify-lg-center align-center pt-1 pb-1 pl-3 pr-3 mt-lg-0 mt-4 rounded-xl"
                v-bind="attrs" v-on="on" role="button" tabindex="0"
                :class="{ 'event-type-filter-chip--off': !isEventTypeSelected('cancelled') }"
                @click.stop="toggleEventTypeFilter('cancelled')"
                @keydown.enter.prevent="toggleEventTypeFilter('cancelled')"
                @keydown.space.prevent="toggleEventTypeFilter('cancelled')">
                <div class="error rounded-circle" style="height: 14px; width: 14px;"></div>
                <div class="ml-2 font-roboto text-subtitle-2">{{ $t('message.cancelled') }}</div>
              </span>
            </template>
            <span>{{ $t('message.cancelled') }}</span>
          </v-tooltip>

        </v-col>

        <v-col cols="12" lg="2" order="1" order-lg="2">
          <div v-if="professors">
            <members-autocomplete :hide-details="true" :label="$t('message.calendar_admin_plus.filter_by_instructor')"
              v-model="selectedProfessor" :items="professors"
              :background-color="isDarkMode() ? '' : 'plusTextOnSecondary'" />
          </div>
        </v-col>


        <v-col order="3" cols="12" class="pa-0 d-lg-none d-flex justify-center">
          <v-card-actions class="pr-0 mr-0">
            <v-btn @click="displayFilters = !displayFilters" text plain class="pa-0 ma-0 text-lowercase">
              <span class="font-roboto text-body-2 text-decoration-underline text-primary text-uppercase">
                {{
                  !displayFilters ? $t('message.calendar_admin_plus.show_more_filters') :
                    $t('message.calendar_admin_plus.members_hide_filters') }}
              </span>
            </v-btn>
          </v-card-actions>
        </v-col>


      </v-row>
    </v-card-actions>

    <!-- <v-card-actions>
        <v-switch v-show="false" v-model="multiSelect" :label="$t('message.calendar_admin_plus.multi_select_dates')"></v-switch>
        <v-btn elevation="0" small :loading="loadingCalendar" @click="loadCalendar()">
          <v-icon class="mr-1">lgi-sync</v-icon>
          {{ $t("message.calendar_admin_plus.refresh") }}
        </v-btn>
        <v-spacer></v-spacer>
        {{ calendarTimeZone }}
      </v-card-actions> -->
    <!-- <v-card-text>
        <div v-if="multiSelect" class="supporting-text">
          <div>{{ $t("message.calendar_admin_plus.click_multiple_dates_explained") }}</div>
        </div>
        <div v-else-if="addingVisitor">
          <v-alert type="info">{{ $t("message.calendar_admin_plus.adding_visitor_explained", [addingVisitor.name]) }}</v-alert>
        </div>
        <span v-else class="supporting-text">{{ $t("message.calendar_admin_plus.click_date_explained") }}</span>
      </v-card-text> -->
    <v-card-actions v-if="multiSelect">
      <v-btn outlined @click="showAddMultipleEvents()" :disabled="filteredEvents.length < 2" elevation="0"
        color="primary" small>
        <v-icon>mdi-plus-circle-outline</v-icon>
        {{ $t("message.calendar_admin_plus.add_multiple") }}
      </v-btn>
    </v-card-actions>

    <v-row class="px-4 pt-3 pb-5  plusGreyLighten5-md rounded-sm">
      <v-col cols="4" class="d-none d-lg-block">
        <div class="d-flex justify-start align-center">
          <span class="font-roboto font-weight-normal text-h5 mr-lg-4">{{ calendarTitle }}</span>
          <v-btn class="rounded-circle plusIndigoLighten1 plusSurface--text mr-2" icon @click="calendarPrev">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <v-btn icon class="rounded-circle plusIndigoLighten1 plusSurface--text" @click="calendarNext">
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </div>
      </v-col>
      <v-col cols="12" lg="4" class="d-flex justify-center align-center">
        <div class="view-toggle-segmented">
          <button type="button" class="view-toggle-segment view-toggle-segment--left"
            :class="{ 'view-toggle-segment--active': currentView === (viewMode === 'list' ? 'listDay' : 'dayGridDay') }"
            @click="switchToGranularView('day')">
            <span class="view-toggle-label">{{ $t('message.calendar_admin_plus.granular_view_day') }}</span>
          </button>
          <button type="button" class="view-toggle-segment view-toggle-segment--left"
            :class="{ 'view-toggle-segment--active': currentView === (viewMode === 'list' ? 'listWeek' : 'dayGridWeek') }"
            @click="switchToGranularView('week')">
            <span class="view-toggle-label">{{ $t('message.calendar_admin_plus.granular_view_week') }}</span>
          </button>
          <button type="button" class="view-toggle-segment view-toggle-segment--right"
            :class="{ 'view-toggle-segment--active': currentView === (viewMode === 'list' ? 'listMonth' : 'dayGridMonth') }"
            @click="switchToGranularView('month')">
            <span class="view-toggle-label">{{ $t('message.calendar_admin_plus.granular_view_month') }}</span>
          </button>
        </div>
      </v-col>
      <v-col cols="12" lg="4" class="d-flex justify-center align-center plusGreyLighten5 rounded-sm ">
        <div class="d-flex justify-end align-center font-roboto text-caption">
          <v-icon size="24" color="plusGreyDarken1">mdi-map-marker-outline</v-icon>
          {{ calendarTimeZone }}
        </div>
      </v-col>
    </v-row>

    <CalendarSvgLoader v-if="loading" />
    <div v-show="!loading" :class="{ 'dark-calendar': isDarkMode() }">
      <CalendarSvgLoader v-if="loadingCalendar" />
      <div v-show="!loadingCalendar">
        <!-- Custom list view (replaces FullCalendar listWeek, listDay, listMonth) -->
        <div v-if="currentView === 'listWeek' || currentView === 'listDay' || currentView === 'listMonth'"
          class="custom-list-view" :class="{ 'custom-list-view--dark': isDarkMode() }">

          <div v-for="group in eventsGroupedByDate" :key="group.date.toISOString?.() ?? group.date" class=""
            :class="{ 'custom-list-date-card': (!isSmallerDisplay()) }">

            <div
              class="custom-list-date-header font-roboto text-md-h5 font-md-weight-regular font-weight-medium text-body-1 plusGreyDarken2-lg--text primary--text my-md-0 my-4">
              {{ $d(group.date, "month_day_year", $i18n.locale) }}
            </div>
            <CalendarListEvents :events="group.events" @event-click="handleListEventClick" />
          </div>

          <div v-if="eventsGroupedByDate.length === 0 && !loadingCalendar"
            class="custom-list-empty text-center py-12 plusGreyDarken1--text">
            {{ $t('message.calendar_admin_plus.no_events_in_period') }}
          </div>
        </div>
        <!-- FullCalendar  -->
        <div v-show="currentView !== 'listWeek' && currentView !== 'listDay' && currentView !== 'listMonth'"
          ref="calendar" class="calendar-div">
        </div>
      </div>
    </div>

    <v-dialog v-model="calendarEventDialog" max-width="90%" v-if="selectedEvent || filteredEvents.length > 2">
      <v-card v-if="loadingCalendar" class="pa-2" elevation="0" outlined>
        <v-card-text class="text-center">
          <div>
            {{ $t('message.calendar_admin_plus.loading_calendar') }}
          </div>
          <div>
            <v-progress-circular size="30" indeterminate color="secondary"></v-progress-circular>
          </div>
        </v-card-text>
        <v-card-actions v-if="selectedEvent" v-allowed="'edit_calendar'">
          <v-btn small elevation="0" @click.stop="closeCalendarEventDialog()">
            {{ $t("message.calendar_admin_plus.close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
      <v-card v-else class="pa-2" elevation="0" outlined>
        <v-card-actions v-if="selectedEvent" class="pb-0 mb-0">
          <v-switch hide-details class="mr-2" v-if="!selectedEvent.vacation"
            :label="$t('message.calendar_admin_plus.disabled')" v-model="selectedEvent.disabled"></v-switch>
          <v-switch hide-details v-if="!selectedEvent.disabled" :label="$t('message.calendar_admin_plus.vacation')"
            v-model="selectedEvent.vacation"></v-switch>
        </v-card-actions>
        <v-card-title class="text-h6" v-if="selectedEvent && !selectedEvent.addingMultiple">
          {{ $d(selectedEvent.date, "month_day_year", $i18n.locale) }}
        </v-card-title>
        <div v-if="selectedEvent && !selectedEvent.disabled">
          <v-card-text class="pb-0">
            <v-text-field :disabled="selectedEvent.existing ? true : false" width="100%" outlined dense
              v-model="selectedEvent.title" :label="$t('message.calendar_admin_plus.title')"></v-text-field>
            <members-autocomplete :label="$t('message.calendar_admin_plus.professor')"
              v-model="selectedEvent.professorUser" />
            <v-text-field v-if="!selectedEvent.vacation" width="20%" v-model="selectedEvent.maxCapacity" type="number"
              outlined dense :label="$t('message.calendar_admin_plus.max_capacity')"></v-text-field>
            <v-text-field hide-details v-if="!selectedEvent.vacation" type="number" width="20%" outlined dense
              v-model="selectedEvent.duration"
              :label="$t('message.calendar_admin_plus.duration') + ' (' + $t('message.calendar_admin_plus.minutes') + ')'"></v-text-field>
            <v-switch hide-details v-if="!selectedEvent.vacation" v-model="selectedEvent.allowNonMembers"
              :label="$t('message.calendar_admin_plus.allow_non_members_join')"></v-switch>
            <div class="text-caption">{{ $t('message.calendar_admin_plus.allow_non_members_join_explained') }}</div>
            <v-switch hide-details v-if="!selectedEvent.vacation" v-model="selectedEvent.skipApproval"
              :label="$t('message.calendar_admin_plus.skip_approval')"></v-switch>
            <div class="text-caption">{{ $t('message.calendar_admin_plus.skip_approval_explained') }}</div>
            <v-textarea class="mt-2" rows="2" width="100%" outlined v-model="selectedEvent.notes"
              :label="$t('message.calendar_admin_plus.notes')"></v-textarea>
            <VisitorsAutocomplete :visitors="selectedEvent.visitors" :multiple="true"
              :label="$t('message.calendar_admin_plus.add_visitor')" v-model="selectedEvent.visitors" />
          </v-card-text>
          <v-card-text class="text-left pt-0 mt-0">
            <div>
              <div class="text-subtitle-2">{{ $t("message.calendar_admin_plus.date") }}:</div>
              <DatePicker :disabled="selectedEvent.existing ? true : false" :show-time="true"
                v-model="selectedEvent.date" :past-event="false">
              </DatePicker>
              <span class="text-caption" v-if="selectedEvent.existing">{{
                $t("message.calendar_admin_plus.cant_edit_date")
                }}</span>
            </div>
            <div v-if="selectedEvent.vacation">
              <div class="text-subtitle-2">{{ $t("message.calendar_admin_plus.end_date") }}:</div>
              <DatePicker :show-time="false" v-model="selectedEvent.endDate" :past-event="false">
              </DatePicker>
            </div>
          </v-card-text>
        </div>
        <v-card-text v-else-if="selectedEvent">
          <div class="text-subtitle-2">{{ $t("message.calendar_admin_plus.date") }}:</div>
          <DatePicker :disabled="selectedEvent.existing ? true : false" :show-time="true" v-model="selectedEvent.date"
            :past-event="false">
          </DatePicker>
          <span class="text-caption" v-if="selectedEvent.existing">{{ $t("message.calendar_admin_plus.cant_edit_date")
            }}</span>
        </v-card-text>
        <v-card-actions v-if="selectedEvent" v-allowed="'edit_calendar'">
          <v-btn :loading="addingCalendarEvent" outlined v-if="!selectedEvent.existing" color="primary" elevation="0"
            small @click="addCalendarEvent()">{{
              multiSelect ? $t("message.calendar_admin_plus.add_multiple") + ' (' + this.filteredEvents.length + ')' :
                $t("message.calendar_admin_plus.add")
            }}</v-btn>
          <v-btn :loading="addingCalendarEvent" outlined v-else color="primary" elevation="0" small
            @click="addCalendarEvent()">{{ $t("message.calendar_admin_plus.update")
            }}</v-btn>
          <v-btn color="error" elevation="0" small v-if="selectedEvent.id != ''" @click.stop="removeEvent()">{{
            $t("message.calendar_admin_plus.delete") }}</v-btn>
          <v-btn small elevation="0" @click.stop="closeCalendarEventDialog()">
            {{ $t("message.calendar_admin_plus.close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import DatePicker from "@/components/inputs/DateTimePicker.vue";
import MembersAutocomplete from "@/components/inputs/MembersAutocomplete.vue";
import VisitorsAutocomplete from "@/components/inputs/VisitorsAutocomplete.vue";
import CalendarListEvents from "@/components/plus/calendar/CalendarListEvents.vue";
import CalendarSvgLoader from "@/components/plus/calendar/CalendarSvgLoader.vue";
import Api from "@/services/Api2";
import { completeOnboardingStep } from "@/services/onboardingSteps";

export default {
  components: {
    DatePicker,
    MembersAutocomplete,
    VisitorsAutocomplete,
    CalendarListEvents,
    CalendarSvgLoader,
  },
  props: {
    activeTab: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      loading: true,
      skipApproval: false,
      allowWaitlist: false,
      professors: null,
      selectedProfessor: null,
      multiSelect: false,
      loadingCalendar: false,
      calendarTimeZone: null,
      timezoneOffset: null,
      addingVisitor: null,
      filteredEvents: [],
      events: [],
      todaysVisitors: [],
      loadingTodaysVisitors: false,
      todaysVisitorsDialog: false,
      calendarInstance: null,
      visitorId: this.$route.query.v,
      calendarEventDialog: false,
      selectedEvent: null,
      addingCalendarEvent: false,
      calendarTitle: '',
      currentView: 'dayGridMonth',
      viewMode: 'calendar',
      listViewRange: { start: null, end: null },
      prevView: null,
      displayFilters: false,
      /** API backgroundColor keys; must match getMappedEventCalendarColors */
      selectedEventTypeKeys: ['f5f5f5', '8d70b2', 'fd9927', 'ec7063', 'cancelled'],
    }
  },
  computed: {
    eventsGroupedByDate() {
      const isListView = this.currentView === 'listWeek' || this.currentView === 'listDay' || this.currentView === 'listMonth';
      if (!isListView || !this.listViewRange.start || !this.listViewRange.end) {
        return [];
      }
      const start = this.listViewRange.start.getTime();
      const end = this.listViewRange.end.getTime();
      const inRange = this.filteredEvents.filter(e => {
        const t = e.start?.getTime?.() ?? new Date(e.start).getTime();
        return t >= start && t < end;
      });
      const groups = {};
      inRange.forEach(ev => {
        const d = ev.start;
        const key = d.toDateString?.() ?? new Date(d).toDateString();
        if (!groups[key]) groups[key] = { date: d, events: [] };
        groups[key].events.push(ev);
      });
      return Object.values(groups).sort((a, b) => a.date - b.date);
    }
  },
  created() {
    Api()
      .get("admins/academy/" + this.$store.state.selectedAcademy)
      .then((response) => {
        const data = response.data;
        if (data && data.calendarSettings) {
          this.skipApproval = data.calendarSettings.skipApproval;
          this.allowWaitlist = data.calendarSettings.allowWaitlist;
        }
        this.loading = false;
      })
      .catch(() => {
        this.loading = false;
      });

    Api()
      .get(
        "calendar/admin/academy/" +
        this.$store.state.managedAcademy +
        "/professors?get_all=true"
      )
      .then((response) => {
        if (response.data && response.data.professors.length > 0) {
          this.professors = response.data.professors;
        }
      });

    if (this.visitorId) {
      Api()
        .get(
          "admins/academy/" +
          this.$store.state.managedAcademy +
          "/visitor/" +
          this.visitorId +
          "/data"
        )
        .then(response => {
          if (response.data) {
            this.addingVisitor = { name: response.data.name, _id: response.data._id }
          }
        });
    }
  },
  mounted() {
    this.loadCalendar(true);
  },
  watch: {
    multiSelect: {
      immediate: false,
      handler: function (value) {
        if (value == true) {
          this.filteredEvents = [];
        } else {
          //this.prepareEvents(this.events);
        }
      },
    },
    selectedProfessor: {
      immediate: false,
      handler: function () {
        this.applyCalendarFilters();
      },
    },
    currentView(newView) {
      const wasListView = this.prevView === 'listWeek' || this.prevView === 'listDay' || this.prevView === 'listMonth';
      const isCalendarView = newView === 'dayGridMonth' || newView === 'dayGridDay' || newView === 'dayGridWeek';
      if (wasListView && isCalendarView && this.calendarInstance) {
        this.$nextTick(() => {
          setTimeout(() => {
            this.calendarInstance?.updateSize?.();
          }, 0);
        });
      }
      this.prevView = newView;
    },
  },
  methods: {
    handleListEventClick(event) {
      if (this.multiSelect) return;
      const mockEvent = {
        event: {
          id: event.id,
          title: event.extendedProps?.titleOriginal || event.title,
          start: event.start,
          startEditable: !event.isEvent,
          extendedProps: {
            titleOriginal: event.extendedProps?.titleOriginal || event.title,
            professor: event.professor || event.extendedProps?.professor,
            notes: event.notes || event.extendedProps?.notes,
            scheduleClassId: event.scheduleClassId || event.extendedProps?.scheduleClassId,
            startNumber: event.startNumber || event.extendedProps?.startNumber
          }
        }
      };
      this.eventClick(mockEvent);
    },
    calendarPrev() {
      this.calendarInstance?.prev();
    },
    calendarNext() {
      this.calendarInstance?.next();
    },
    switchView(viewType) {
      this.calendarInstance?.changeView(viewType);
    },
    setViewMode(mode) {
      this.viewMode = mode;
      if (mode === 'calendar') {
        this.switchView('dayGridMonth');
      } else {
        this.switchView('listWeek');
      }
    },
    switchToGranularView(granularity) {
      if (granularity === 'day' || granularity === 'week') {
        this.viewMode = 'list';
      }
      const viewMap = {
        day: 'listDay',
        week: 'listWeek',
        month: this.viewMode === 'list' ? 'listMonth' : 'dayGridMonth'
      };
      this.switchView(viewMap[granularity]);
    },
    loadCalendar(loadAll) {
      this.loadingCalendar = true;
      if (loadAll) {
        let fullCalendarScript = document.createElement('script')
        fullCalendarScript.setAttribute('src', 'https://cdn.jsdelivr.net/npm/fullcalendar@6.1.4/index.global.min.js')
        document.head.appendChild(fullCalendarScript);
        const vm = this;
        fullCalendarScript.addEventListener('load', () => {
          //Locales
          const localeEs = document.createElement("script");
          localeEs.src = 'https://cdn.jsdelivr.net/npm/fullcalendar@5.11.3/locales/es.js';
          localeEs.type = 'text/javascript';
          document.head.appendChild(localeEs);

          const localePt = document.createElement("script");
          localePt.src = 'https://cdn.jsdelivr.net/npm/fullcalendar@5.11.3/locales/pt.js';
          localePt.type = 'text/javascript';
          document.head.appendChild(localePt);
          vm.getEvents();
        });
      } else {
        this.getEvents();
      }
    },
    getEvents() {
      this.loadingCalendar = true;
      Api()
        .get(
          "calendar/admin/academy/" +
          this.$store.state.managedAcademy +
          "/events"
        )
        .then((response) => {
          const data = response.data;
          if (data) {
            this.calendarTimeZone = data.timeZone;
            this.timezoneOffset = this.getTimezoneOffset(data.timeZone);
            this.events = data.events;
            this.initializeCalendar(data.events);
            this.loadingCalendar = false;
            this.loadTodaysVisitors();
            this.$emit('calendar-loaded');
          }
        });
    },
    initializeCalendar(events) {
      const preparedEvents = this.prepareEvents(events);
      let calendarOptions = {
        initialView: this.isSmallerDisplay() ? 'listWeek' : 'dayGridMonth',
        events: preparedEvents,
        views: {
          dayGridDay: { buttonText: this.$t('message.calendar_admin_plus.granular_view_day') },
          dayGridWeek: { buttonText: this.$t('message.calendar_admin_plus.granular_view_week') },
          dayGridMonth: { buttonText: this.$t('message.calendar_admin_plus.granular_view_month') },
          listDay: { buttonText: this.$t('message.calendar_admin_plus.granular_view_day') },
          listWeek: { buttonText: this.$t('message.calendar_admin_plus.granular_view_week') },
          listMonth: { buttonText: this.$t('message.calendar_admin_plus.granular_view_month') },
        },
        headerToolbar: false,
        // this.isSmallerDisplay()
        //   ? { left: 'prev,next', center: '', right: 'listWeek,dayGridMonth' }
        //   : { left: '', center: 'title', right: 'prev,next' }
      };
      // if (this.isSmallerDisplay()) {
      //   calendarOptions.headerToolbar = {
      //     left: 'prev,next',
      //     center: '',
      //     right: 'listWeek,dayGridMonth'
      //   };
      // }
      this.filteredEvents = preparedEvents;
      let userLang = this.$i18n.locale;
      if (userLang == 'es')
        calendarOptions.locale = userLang;
      else if (userLang == 'pt')
        calendarOptions.locale = userLang;

      const mountWhenReady = (attempts = 0) => {
        if (this._isDestroyed) return;
        const calendarEl = this.$refs.calendar;
        if (!calendarEl) {
          if (attempts > 80) return;
          if (this.loading) {
            const stop = this.$watch('loading', (val) => {
              if (!val) {
                stop();
                this.$nextTick(() => mountWhenReady(attempts));
              }
            });
            return;
          }
          this.$nextTick(() => mountWhenReady(attempts + 1));
          return;
        }

        if (this.calendarInstance) {
          try {
            this.calendarInstance.destroy();
          } catch (e) {
            console.error(e);
          }
          this.calendarInstance = null;
        }

        /*eslint-disable no-undef*/
        this.calendarInstance = new FullCalendar.Calendar(calendarEl, calendarOptions);

        this.calendarInstance.on('eventClick', (info) => {
          this.eventClick(info);
        });
        this.calendarInstance.on('dateClick', (info) => {
          this.dateClick(info);
        });
        this.calendarInstance.on('datesSet', (dateInfo) => {
          this.prevView = this.currentView;
          this.calendarTitle = dateInfo.view.title;
          this.currentView = dateInfo.view.type;
          this.viewMode = (dateInfo.view.type === 'listDay' || dateInfo.view.type === 'listWeek' || dateInfo.view.type === 'listMonth') ? 'list' : 'calendar';
          this.listViewRange = {
            start: dateInfo.view.currentStart,
            end: dateInfo.view.currentEnd
          };
        });
        this.calendarInstance.render();
        this.applyCalendarFilters();
      };

      this.$nextTick(mountWhenReady);
    },
    getMappedEventCalendarColors(event) {
      const mapColors = {
        fd9927: { color: '#F57F17', backgroundColor: '#FFF8E1' },
        '8d70b2': { color: '#6A1B9A', backgroundColor: '#F3E5F5' },
        ec7063: { color: '#4CAF50', backgroundColor: '#E8F5E9' },
        f5f5f5: { color: '#757575', backgroundColor: '#EEE' },
      };
      const key = (event.backgroundColor || '').toString().toLowerCase().replace('#', '');
      const mapped = mapColors[key];
      if (mapped) return { color: mapped.color, backgroundColor: mapped.backgroundColor };
      return null;
    },
    async loadTodaysVisitors() {
      this.loadingTodaysVisitors = true;
      const todayStr = new Date().toDateString();
      // Only classes with a live AcademyClass doc (event.id) can have visitors
      // at all — this also naturally excludes classes with nothing recorded yet.
      const classesToday = (this.events || []).filter(e => {
        if (!e.id) return false;
        const raw = e.start || e.date;
        if (!raw) return false;
        return new Date(raw).toDateString() === todayStr;
      });
      const results = await Promise.all(classesToday.map(e =>
        Api()
          .get("calendar/admin/academy/" + this.$store.state.managedAcademy + "/classes/" + e.id)
          .then(response => ({ event: e, academyClass: response.data && response.data.academyClass }))
          .catch(() => null)
      ));
      const rows = [];
      for (const r of results) {
        if (!r || !r.academyClass || !r.academyClass.visitors || !r.academyClass.visitors.length) continue;
        const classTime = new Date(r.event.start || r.event.date);
        for (const v of r.academyClass.visitors) {
          rows.push({
            visitorId: v._id,
            visitorName: v.name,
            className: r.event.titleOriginal || r.event.title,
            classTime,
          });
        }
      }
      rows.sort((a, b) => a.classTime - b.classTime);
      this.todaysVisitors = rows;
      this.loadingTodaysVisitors = false;
    },
    formatTodaysVisitorTime(d) {
      return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    },
    prepareEvents(eventsToDisplay) {
      if (!eventsToDisplay)
        return;

      return eventsToDisplay.map(e => {
        const newE = { ...e };

        let eventDate = newE.date || newE.start;
        newE.display = 'block';
        let d = new Date(eventDate);
        d.setHours(d.getHours() + this.timezoneOffset);

        if ((newE.vacation || newE.isEvent) && newE.endDate) {
          let eventEndDate = newE.endDate;
          let d2 = new Date(eventEndDate);
          d2.setHours(d2.getHours() + this.timezoneOffset);

          if (newE.vacation) {
            d.setHours(0);
            d.setMinutes(0);
            newE.allDay = true;//Not sure why this has to be like this but works
          }
          newE.end = d2;
        }

        newE.start = d;

        const mapped = this.getMappedEventCalendarColors(newE);
        if (mapped) {
          newE.backgroundColor = mapped.color;
          newE.borderColor = mapped.backgroundColor;
          newE.eventColor = mapped.backgroundColor;
          newE.textColor = "#FFFFFF";
        }

        if (newE.disabled) {
          const errorColor = (this.$vuetify && this.$vuetify.theme.currentTheme.error) || '#f44336';
          newE.backgroundColor = errorColor;
          newE.borderColor = errorColor;
          newE.textColor = '#FFFFFF';
        }

        return newE;
      });
    },
    showAddMultipleEvents() {
      this.calendarEventDialog = true;
      this.selectedEvent = {
        id: '',
        addingMultiple: true,
        date: this.filteredEvents ? this.filteredEvents[0].start : new Date()
      };
    },
    normalizeEventBgKey(bg) {
      return (bg || '').toString().toLowerCase().replace('#', '');
    },
    isEventTypeSelected(key) {
      return this.selectedEventTypeKeys.includes(key);
    },
    toggleEventTypeFilter(key) {
      const i = this.selectedEventTypeKeys.indexOf(key);
      if (i === -1) this.selectedEventTypeKeys.push(key);
      else this.selectedEventTypeKeys.splice(i, 1);
      this.applyCalendarFilters();
    },
    applyCalendarFilters() {
      if (!this.events || !this.calendarInstance) return;
      let list = this.events;
      if (this.selectedProfessor) {
        list = list.filter(
          e => e.professor && e.professor._id && e.professor._id.toString() === this.selectedProfessor
        );
      }
      const knownKeys = new Set(['fd9927', '8d70b2', 'ec7063', 'f5f5f5']);
      const selected = new Set(this.selectedEventTypeKeys);
      const showCancelled = selected.has('cancelled');
      list = list.filter(e => {
        if (e.disabled) return showCancelled;
        const k = this.normalizeEventBgKey(e.backgroundColor);
        if (!k || !knownKeys.has(k)) return true;
        return selected.has(k);
      });
      this.updateCalendarEvents(list);
    },
    updateCalendarEvents(events) {
      if (!this.calendarInstance) {
        console.error("Calendar not initialized!");
        return;
      }
      const preparedEvents = this.prepareEvents(events)
      this.calendarInstance.setOption('events', preparedEvents);
      this.filteredEvents = preparedEvents;
    },
    dateClick(e) {
      if (!this.multiSelect) {
        let d = e.date;
        d.setHours(10);
        d.setMinutes(0);
        this.selectedEvent = {
          id: "",
          date: d,
          endDate: new Date(d),
          skipApproval: this.skipApproval
        };
        this.calendarEventDialog = true;
      } else {
        try {
          const start = e.date;
          const now = new Date();
          now.setDate(now.getDate() - 1);
          now.setHours(23);
          now.setMinutes(59);
          now.setSeconds(59);
          if (now < start) {
            let index = 0;
            for (const d of this.filteredEvents) {
              if (d.start.getTime() == start.getTime()) {
                this.filteredEvents.splice(index, 1);
                return;
              }
              index++;
            }
            this.filteredEvents.push({
              start: start,
              block: true,
              title: this.$t("message.calendar_admin_plus.selected"),
              editable: false,
            });
          }
        } catch (err) {
          console.log(err)
        }
      }
    },
    eventClick(e) {
      if (this.multiSelect)
        return;//nothing to do here
      if (e.event.id && e.event.startEditable) {
        this.navigateTo({
          name: "manageacademyclass",
          params: { academyClassId: e.event.id },
        });
      } else {
        if (e.event.startEditable) {
          this.selectedEvent = { existing: true };
          this.selectedEvent.id = e.event.id;
          this.selectedEvent.start = e.event.start;
          this.selectedEvent.skipApproval = this.skipApproval;
          this.selectedEvent.allowWaitlist = this.allowWaitlist;
          this.selectedEvent.date = e.event.start;
          this.selectedEvent.title = e.event.extendedProps.titleOriginal
            ? e.event.extendedProps.titleOriginal
            : e.event.title;
          this.selectedEvent.startNumber = e.event.extendedProps.startNumber;
          this.selectedEvent.professor = e.event.extendedProps.professor;
          this.selectedEvent.professorUser = e.event.extendedProps.professor ? e.event.extendedProps.professor._id : null;
          this.selectedEvent.notes = e.event.extendedProps.notes;
          this.selectedEvent.scheduleClassId = e.event.extendedProps.scheduleClassId;
          if (this.visitorId)
            this.selectedEvent.visitors = [this.visitorId];
          this.calendarEventDialog = true;
        } else {
          this.goToAcademyEvent(e.event.id);
        }
      }
    },
    goToAcademyEvent(id) {
      this.navigateTo({
        name: "manageacademyeventplus",
        params: { eventId: id },
      });
    },
    closeCalendarEventDialog() {
      this.calendarEventDialog = false;
      this.selectedEvent = null;
    },
    removeEvent() {
      Api()
        .delete(
          "calendar/admin/academy/" +
          this.$store.state.managedAcademy +
          "/event/" +
          this.selectedEvent.id
        )
        .then((response) => {
          const data = response.data;
          if (data) {
            this.showAlert("success", this.$t("message.calendar_admin_plus.success"));
            this.getEvents();
            this.closeCalendarEventDialog();
          }
        });
    },
    getStartNumber() {
      if (this.selectedEvent.date) {
        const d = new Date(this.selectedEvent.date);
        const hours = d.getHours();
        const minutes = d.getMinutes();
        return parseFloat(hours + "." + String(minutes).padStart(2, "0"));
      }
    },
    addCalendarEvent() {
      if (!this.selectedEvent.date) {
        this.showAlert("error", this.$t("message.calendar_admin_plus.add_time"));
        return;
      }
      if (
        this.selectedEvent.maxCapacity &&
        this.selectedEvent.maxCapacity != "" &&
        isNaN(this.selectedEvent.maxCapacity)
      ) {
        this.showAlert("error", this.$t("message.calendar_admin_plus.max_capacity_nan"));
        return;
      }
      this.addingCalendarEvent = true;
      if (!this.selectedEvent.startNumber) {
        this.selectedEvent.startNumber = this.getStartNumber();
      }
      if (
        this.selectedEvent.duration &&
        this.selectedEvent.duration != "" &&
        isNaN(this.selectedEvent.duration)
      ) {
        this.selectedEvent.duration = null;
      }
      let multipleDays = null;
      const date = this.selectedEvent.start
        ? this.selectedEvent.start
        : this.selectedEvent.date;
      let adjustedDate = this.adjustTimeZone(new Date(date), this.timezoneOffset, true);
      let adjustedEndDate = null;
      if (this.multiSelect && this.filteredEvents.length > 1) {
        multipleDays = [];
        for (const d of this.filteredEvents) {
          let eventDate = new Date(d.start);
          eventDate.setMinutes(date.getMinutes());
          eventDate.setHours(date.getHours());
          eventDate.setSeconds(date.getSeconds());
          multipleDays.push(eventDate);
        }
      }
      if (this.selectedEvent.vacation) {
        adjustedEndDate = this.adjustTimeZone(new Date(this.selectedEvent.endDate), this.timezoneOffset, true);
        adjustedEndDate.setHours(23);
        adjustedEndDate.setMinutes(50);
      }
      const data = {
        title: this.selectedEvent.title,
        disabled: this.selectedEvent.disabled,
        vacation: this.selectedEvent.vacation,
        professorUser: this.selectedEvent.professorUser,
        duration: this.selectedEvent.duration,
        scheduleClassId: this.selectedEvent.scheduleClassId,
        allowNonMembers: this.selectedEvent.allowNonMembers,
        date: adjustedDate,
        endDate: adjustedEndDate,
        startNumber: this.getStartNumber(),
        bulkAdded: this.multiSelect ? true : false,
        notes: this.selectedEvent.notes,
        days: multipleDays,
        skipApproval: this.selectedEvent.skipApproval,
        allowWaitlist: this.selectedEvent.allowWaitlist,
        maxCapacity: this.selectedEvent.maxCapacity,
        visitors: this.selectedEvent.visitors && this.selectedEvent.visitors.length ? this.selectedEvent.visitors : null
      };
      Api()
        .post(
          "calendar/admin/academy/" +
          this.$store.state.managedAcademy +
          "/event",
          data
        )
        .then((response) => {
          if (response) {
            const createdCount = typeof response.data?.total === "number" ? response.data.total : 1;
            if (createdCount > 0) {
              this.showAlert("success", this.$t("message.calendar_admin_plus.events_created", [createdCount]));
              completeOnboardingStep({
                step: "setupSchedule",
                managedAcademy: this.$store.state.managedAcademy,
                store: this.$store
              });
            }
            else
              this.showAlert("success", this.$t("message.calendar_admin_plus.success"));
            this.multiSelect = false;
            this.getEvents();
            this.closeCalendarEventDialog();
            this.addingCalendarEvent = false;
          }
        })
        .catch(() => {
          this.addingCalendarEvent = false;
          this.showAlert("error", this.$t("message.calendar_admin_plus.error_generic"));
        });
    }
  }
}
</script>

<style lang="scss" scoped>
@import "https://cdn.jsdelivr.net/npm/fullcalendar@5.11.3/main.min.css";

.event-type-filter-chip {
  cursor: pointer;
  user-select: none;
  outline: none;
  transition: opacity 0.2s ease;

  &--off {
    opacity: 0.45;
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px rgba(57, 73, 171, 0.45);
  }

  .theme--dark & {
    color: #212121;
  }
}

.view-toggle-segmented {
  display: inline-flex;
  border: 1px solid #C5C5C5;
  box-sizing: border-box;
  border-radius: 6px;
  overflow: hidden;
  background: #FFFFFF;

  .theme--dark & {
    background: #1E1E1E;
    border-color: rgba(255, 255, 255, 0.12);
  }
}

.view-toggle-segment {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 16px;
  min-width: 112px;
  height: 40px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: "Roboto", sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #757575;
  transition: background-color 0.2s, color 0.2s;

  &:hover:not(.view-toggle-segment--active) {
    background-color: #F5F5F5;
  }

  &--left {
    border-right: 1px solid #C5C5C5;
  }

  &--right {
    border-radius: 0 5px 5px 0;
  }

  &--active {
    background-color: #3949AB;
    color: #FFFFFF;
  }

  .theme--dark & {
    color: #BDBDBD;

    &:hover:not(.view-toggle-segment--active) {
      background-color: rgba(255, 255, 255, 0.05);
    }

    &.view-toggle-segment--left {
      border-right-color: rgba(255, 255, 255, 0.12);
    }
  }
}

.custom-calendar-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  font-family: "Roboto", sans-serif;
  font-size: 18px;

  .calendar-title {
    flex: 1;
    text-align: center;
    font-weight: 500;
  }
}

.calendar-div {
  min-height: 480px;
}

.custom-list-view {
  min-height: 480px;
}

.custom-list-date-card {
  background: #fff;
  border-radius: 8px;
  border: 1px solid #E0E0E0;
  padding: 24px;
  margin-bottom: 24px;
  overflow: hidden;
}

.custom-list-empty {
  font-size: 14px;
}

.custom-list-view--dark .custom-list-date-card {
  background: #424242;
  border-color: rgba(255, 255, 255, 0.12);
}


.dark-calendar ::v-deep .fc .fc-col-header-cell {
  background-color: #2D2D2D !important;
  color: #BDBDBD !important;
  border-color: rgba(255, 255, 255, 0.12) !important;
}

.dark-calendar ::v-deep .fc .fc-daygrid-day {
  background-color: #1E1E1E !important;
}

.dark-calendar ::v-deep .fc .fc-daygrid-day.fc-day-other,
.dark-calendar ::v-deep .fc .fc-daygrid-day.fc-day-other .fc-daygrid-day-frame {
  background-color: #161616 !important;
}

.dark-calendar ::v-deep .fc .fc-daygrid-day-number {
  color: #BDBDBD !important;
}

.dark-calendar ::v-deep .fc .fc-day-today {
  background-color: rgba(255, 255, 255, 0.06) !important;
}

.dark-calendar ::v-deep .fc .fc-scrollgrid,
.dark-calendar ::v-deep .fc .fc-scrollgrid td,
.dark-calendar ::v-deep .fc .fc-scrollgrid th {
  border-color: rgba(255, 255, 255, 0.1) !important;
}

.dark-calendar ::v-deep .fc .fc-list-day-cushion {
  background-color: #2D2D2D !important;
  color: #BDBDBD !important;
}

.dark-calendar ::v-deep .fc .fc-list-event:hover td {
  background-color: rgba(255, 255, 255, 0.05) !important;
}

.mobile-header-border {
  border: 1px solid #E8EAF6 !important;
  background-color: #FAFAFA;
  padding: 1.25rem 1rem 1.25rem 1rem !important;

  .theme--dark & {
    background-color: #2C2C2C;
    border-color: rgba(255, 255, 255, 0.12) !important;
  }
}

::v-deep .fc .fc-col-header-cell {
  background-color: #E0E0E0 !important;
  color: #000 !important;
  font-family: "Roboto", sans-serif !important;
  font-size: 12px !important;
  text-transform: uppercase !important;
  font-weight: 400 !important;
  border: 1px solid #C5C5C5 !important;
}

::v-deep .fc .fc-daygrid-day.fc-day-other,
::v-deep .fc .fc-daygrid-day.fc-day-other .fc-daygrid-day-frame {
  background-color: #f7f7f7 !important;
}

::v-deep .fc .fc-daygrid-day-number {
  float: none !important;
  display: block;
  text-align: center;
  width: 100%;
  margin: 0;
  font-family: "Roboto", sans-serif !important;
  font-size: 14px !important;
  padding: 0.5rem 0;
}

::v-deep .fc .fc-daygrid-event {
  margin-right: 8px !important;
  margin-bottom: 4px !important;
  border-radius: 4px !important;
  padding-left: 4px !important;
  padding-right: 4px !important;
  font-family: "Roboto", sans-serif !important;
  font-size: 12px !important;
  font-weight: 400 !important;
}

::v-deep .fc-header-toolbar.fc-header-toolbar {
  width: 100% !important;
  flex-direction: row !important;
  justify-content: start !important;
  font-family: "Roboto-Medium", sans-serif !important;
  font-size: 18px !important;
  font-weight: 400 !important;
}


::v-deep .fc .fc-prev-button,
::v-deep .fc .fc-next-button {
  width: 36px !important;
  height: 36px !important;
  padding: 0 !important;
  border-radius: 50% !important;
  border: none !important;
  box-shadow: none !important;
  background-color: #5C6BC0 !important;
  background-image: none !important;
  margin: 0 4px !important;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
}

::v-deep .fc .fc-prev-button {
  margin-left: 16px !important;
}


::v-deep .fc .fc-prev-button:hover,
::v-deep .fc .fc-next-button:hover {
  background-color: #3949AB !important;
  background-image: none !important;
  border: none !important;
  box-shadow: none !important;
}

::v-deep .fc .fc-prev-button .fc-icon,
::v-deep .fc .fc-next-button .fc-icon {
  color: #FFFFFF !important;
  font-size: 1.1em !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}
</style>
